import React, { useState } from 'react';
import { Box, IconButton, MenuItem, Popover, Stack, Tooltip, Typography } from '@mui/material';
import PaletteIcon from '@mui/icons-material/Palette';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import CheckIcon from '@mui/icons-material/Check';

import { useThemeManager } from '../../../hooks/useThemeManager';
import type { ThemeId } from '../../../contexts/ThemeContext';

/**
 * Compact horizontal-toolbar theme menu. Adapts the v1 ThemeSwitcher
 * (sized for a vertical sidebar with `width: 100%`) into an icon-button
 * that fits naturally in the TopBar. The popover offers theme + color
 * mode options.
 */
export const ThemeMenu: React.FC = () => {
  const { themeId, colorMode, setThemeId, toggleColorMode, availableThemes } = useThemeManager();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title="Theme">
        <IconButton size="small" onClick={e => setAnchorEl(e.currentTarget)}>
          <PaletteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { p: 1, minWidth: 220 } } }}
      >
        <Stack spacing={0.5}>
          <Typography variant="overline" sx={{ px: 1, opacity: 0.6 }}>Theme</Typography>
          {availableThemes.map(opt => (
            <MenuItem
              key={opt.id}
              dense
              onClick={() => {
                setThemeId(opt.id as ThemeId);
                setAnchorEl(null);
              }}
              sx={{ borderRadius: 1 }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2">{opt.name}</Typography>
                {opt.description && (
                  <Typography variant="caption" color="text.secondary">
                    {opt.description}
                  </Typography>
                )}
              </Box>
              {opt.id === themeId && <CheckIcon fontSize="small" sx={{ ml: 1 }} />}
            </MenuItem>
          ))}
          <Box sx={{ borderTop: theme => `1px solid ${theme.palette.divider}`, my: 0.5 }} />
          <MenuItem dense onClick={toggleColorMode} sx={{ borderRadius: 1 }}>
            {colorMode === 'dark' ? (
              <LightModeIcon fontSize="small" sx={{ mr: 1 }} />
            ) : (
              <DarkModeIcon fontSize="small" sx={{ mr: 1 }} />
            )}
            <Typography variant="body2">
              Switch to {colorMode === 'dark' ? 'light' : 'dark'} mode
            </Typography>
          </MenuItem>
        </Stack>
      </Popover>
    </>
  );
};
